import { createInertiaApp, router } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';
import './css/app.css';
import './i18n';
import { initSentry, Sentry } from './sentry';

const EXTERNAL_DOM_MUTATION_PATTERNS = [
    "Failed to execute 'removeChild' on 'Node'",
    "Failed to execute 'insertBefore' on 'Node'",
    "Failed to execute 'replaceChild' on 'Node'",
    'The node to be removed is not a child of this node',
    'The node before which the new node is to be inserted is not a child of this node',
    'The node to be replaced is not a child of this node',
];
const DOM_MUTATION_RECOVERY_SESSION_KEY = 'external-dom-mutation-recovery-ts';
const DOM_MUTATION_RECOVERY_COOLDOWN_MS = 15000;
const DOM_MUTATION_TELEMETRY_THROTTLE_MS = 2000;

const getErrorMessage = (errorLike) => {
    if (typeof errorLike === 'string') {
        return errorLike;
    }

    if (errorLike && typeof errorLike.message === 'string') {
        return errorLike.message;
    }

    return '';
};

const isExternalDomMutationError = (errorLike) => {
    const message = getErrorMessage(errorLike);
    return EXTERNAL_DOM_MUTATION_PATTERNS.some((pattern) => message.includes(pattern));
};

const trackExternalDomMutation = (kind, details = {}) => {
    const now = Date.now();
    const lastTs = window.__externalDomMutationLastTelemetryTs || 0;

    if (now - lastTs < DOM_MUTATION_TELEMETRY_THROTTLE_MS) {
        return;
    }
    window.__externalDomMutationLastTelemetryTs = now;

    Sentry.addBreadcrumb({
        category: 'dom-mutation-recovery',
        level: 'warning',
        message: `External DOM mutation handled: ${kind}`,
        data: details,
    });
};

const installExternalDomMutationGuard = () => {
    // Browser translators/extensions can mutate React-managed DOM nodes.
    // Guard common mutation APIs so React commits do not crash the UI.
    if (window.__externalDomMutationGuardInstalled) {
        return;
    }
    window.__externalDomMutationGuardInstalled = true;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function patchedRemoveChild(child) {
        if (!child || child.parentNode !== this) {
            return child;
        }

        try {
            return originalRemoveChild.call(this, child);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'NotFoundError') {
                trackExternalDomMutation('removeChild_not_found', {
                    nodeName: this.nodeName,
                    childNodeName: child?.nodeName,
                });
                return child;
            }
            throw error;
        }
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function patchedInsertBefore(newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
            return this.appendChild(newNode);
        }

        try {
            return originalInsertBefore.call(this, newNode, referenceNode);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'NotFoundError') {
                trackExternalDomMutation('insertBefore_not_found', {
                    nodeName: this.nodeName,
                    newNodeName: newNode?.nodeName,
                    referenceNodeName: referenceNode?.nodeName,
                });
                return this.appendChild(newNode);
            }
            throw error;
        }
    };

    const originalReplaceChild = Node.prototype.replaceChild;
    Node.prototype.replaceChild = function patchedReplaceChild(newChild, oldChild) {
        if (!oldChild || oldChild.parentNode !== this) {
            return this.appendChild(newChild);
        }

        try {
            return originalReplaceChild.call(this, newChild, oldChild);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'NotFoundError') {
                trackExternalDomMutation('replaceChild_not_found', {
                    nodeName: this.nodeName,
                    newChildNodeName: newChild?.nodeName,
                    oldChildNodeName: oldChild?.nodeName,
                });
                return this.appendChild(newChild);
            }
            throw error;
        }
    };
};

const recoverFromExternalDomMutationError = () => {
    const errorMessage = 'External DOM mutation conflict detected. Triggering auto-reload recovery.';

    try {
        const now = Date.now();
        const lastRecoveryTs = Number(window.sessionStorage.getItem(DOM_MUTATION_RECOVERY_SESSION_KEY) || 0);

        if (now - lastRecoveryTs < DOM_MUTATION_RECOVERY_COOLDOWN_MS) {
            trackExternalDomMutation('reload_suppressed_cooldown', {
                cooldownMs: DOM_MUTATION_RECOVERY_COOLDOWN_MS,
            });
            return;
        }

        window.sessionStorage.setItem(DOM_MUTATION_RECOVERY_SESSION_KEY, String(now));
    } catch (_error) {
        // Ignore sessionStorage issues and still attempt a reload.
    }

    trackExternalDomMutation('reload_triggered');
    Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('frontend_recovery', 'external_dom_mutation_autoreload');
        Sentry.captureMessage(errorMessage);
    });

    window.location.reload();
};

const reportUnhandledError = (errorLike) => {
    const normalizedError =
        errorLike instanceof Error ? errorLike : new Error(getErrorMessage(errorLike) || String(errorLike));

    if (typeof window.reportError === 'function') {
        window.reportError(normalizedError);
        return;
    }

    setTimeout(() => {
        throw normalizedError;
    }, 0);
};

const installExternalDomMutationFallbackHandler = () => {
    if (window.__externalDomMutationFallbackHandlerInstalled) {
        return;
    }
    window.__externalDomMutationFallbackHandlerInstalled = true;

    window.addEventListener(
        'error',
        (event) => {
            if (!isExternalDomMutationError(event.error || event.message)) {
                return;
            }

            event.preventDefault();
            recoverFromExternalDomMutationError();
        },
        true
    );

    window.addEventListener('unhandledrejection', (event) => {
        if (!isExternalDomMutationError(event.reason)) {
            return;
        }

        event.preventDefault();
        recoverFromExternalDomMutationError();
    });
};

installExternalDomMutationGuard();
installExternalDomMutationFallbackHandler();

function showNetworkErrorToast() {
    if (document.getElementById('inertia-network-toast')) return;
    const toast = document.createElement('div');
    toast.id = 'inertia-network-toast';
    toast.textContent = 'Sin conexión. Verifica tu internet e intenta de nuevo.';
    toast.style.cssText =
        'position:fixed;bottom:1.5rem;left:50%;transform:translateX(-50%);' +
        'background:#dc2626;color:#fff;padding:.75rem 1.5rem;border-radius:.5rem;' +
        'z-index:9999;font-size:.875rem;box-shadow:0 4px 12px rgba(0,0,0,.3);';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
}

router.on('exception', (event) => {
    const isNetworkError =
        event.exception?.message === 'Network Error' || event.exception?.code === 'ERR_NETWORK';
    if (isNetworkError) {
        event.preventDefault();
        showNetworkErrorToast();
    }
});

document.addEventListener('DOMContentLoaded', () => {
    createInertiaApp({
        resolve: (name) => {
            // Exclude colocated test files so they are never bundled into the app
            // (importing "vitest" at runtime breaks the page).
            const pages = import.meta.glob(
                ['./pages/**/*.tsx', '!./pages/**/*.test.tsx'],
                { eager: true }
            );
            return pages[`./pages/${name}.tsx`];
        },
        setup({ el, App, props }) {
            el.setAttribute('translate', 'no');
            el.classList.add('notranslate');

            // Initialize Sentry with config from Django (only present in staging/production)
            const sentryConfig = props.initialPage.props.shared?.sentry;
            initSentry(sentryConfig);

            const root = createRoot(el, {
                onUncaughtError: (error) => {
                    if (isExternalDomMutationError(error)) {
                        recoverFromExternalDomMutationError();
                        return;
                    }

                    reportUnhandledError(error);
                },
                onRecoverableError: (error) => {
                    if (isExternalDomMutationError(error)) {
                        recoverFromExternalDomMutationError();
                        return;
                    }

                    reportUnhandledError(error);
                },
            });

            root.render(<App {...props} />);
        },
    }).then(() => {});
});
